import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Grid,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  TrendingUp,
  Assignment,
  Groups,
  BarChart,
} from "@mui/icons-material";
import ScreenshotImage from "./ScreenshotImage";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 4 }}>{children}</Box>}
    </div>
  );
};

const featureCategories = [
  {
    label: "Planejamento",
    icon: <TrendingUp />,
    screenshot: "/screenshots/swot.png",
    screenshotAlt: "Análise SWOT ThinkWay",
    title: "Planejamento Estratégico Completo",
    description:
      "Construa a base sólida do seu planejamento com ferramentas profissionais de análise e definição estratégica.",
    features: [
      "Análise SWOT com pontuação e radar visual",
      "Análise PESTEL do ambiente externo",
      "Mapa Estratégico visual exportável em PDF",
      "Definição de Missão, Visão e Valores (com IA)",
      "Análise de ambiente interno (forças e fraquezas)",
      "Cálculo automático do índice de favorabilidade",
    ],
  },
  {
    label: "Metas & KPIs",
    icon: <BarChart />,
    screenshot: "/screenshots/metas.png",
    screenshotAlt: "Metas ThinkWay",
    title: "Gestão de Metas e Resultados",
    description:
      "Defina, acompanhe e alcance seus objetivos com metodologias comprovadas e métricas em tempo real.",
    features: [
      "Metas globais com geração automática via IA",
      "Indicadores quantitativos e qualitativos",
      "Acompanhamento mensal com status visual",
      "Metas por área (Vendas, Pessoas, Finanças, Processos)",
      "KPIs com orçado vs realizado",
      "Avaliação de desempenho e alertas automáticos",
    ],
  },
  {
    label: "Projetos",
    icon: <Assignment />,
    screenshot: "/screenshots/thinkboard.png",
    screenshotAlt: "ThinkBoard Kanban",
    title: "Gestão de Projetos e Tarefas",
    description:
      "Organize e execute suas iniciativas estratégicas com eficiência usando metodologias ágeis.",
    features: [
      "ThinkBoard: Kanban drag-and-drop intuitivo",
      "Planos de Ação com filtros avançados",
      "Metodologia 5W2H para detalhamento de tarefas",
      "Visualização em cards ou tabela",
      "Acompanhamento de investimentos por projeto",
      "Arquivamento e histórico de ações",
    ],
  },
  {
    label: "Colaboração",
    icon: <Groups />,
    screenshot: "/screenshots/colaboradores.png",
    screenshotAlt: "Gestão de Colaboradores",
    title: "Trabalho em Equipe",
    description:
      "Engaje sua equipe e mantenha todos alinhados com a estratégia da empresa.",
    features: [
      "Gestão completa de colaboradores",
      "Departamentos e estrutura organizacional",
      "Perfis de acesso personalizáveis",
      "Sistema de notificações em tempo real",
      "PLR vinculado a metas (com IA)",
      "Controle de permissões por tela",
    ],
  },
];

const FeaturesSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      id="features"
      sx={{
        py: { xs: 10, md: 14 },
        backgroundColor: "#F8FAFC",
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#042E4B",
                mb: 2,
                fontSize: { xs: "2rem", md: "2.75rem" },
              }}
            >
              Tudo que você precisa em{" "}
              <Box component="span" sx={{ color: "#EF443C" }}>
                um só lugar
              </Box>
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#64748B",
                fontWeight: 400,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Uma plataforma completa para planejamento estratégico, gestão de
              metas, projetos e colaboração em equipe.
            </Typography>
          </motion.div>
        </Box>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                backgroundColor: "white",
                borderRadius: 3,
                p: 1,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                "& .MuiTabs-indicator": {
                  display: "none",
                },
                "& .MuiTab-root": {
                  minHeight: 48,
                  borderRadius: 2,
                  mx: 0.5,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#64748B",
                  "&.Mui-selected": {
                    backgroundColor: "#042E4B",
                    color: "white",
                  },
                },
              }}
            >
              {featureCategories.map((category) => (
                <Tab
                  key={category.label}
                  icon={category.icon}
                  iconPosition="start"
                  label={category.label}
                />
              ))}
            </Tabs>
          </Box>
        </motion.div>

        {/* Tab Panels */}
        <AnimatePresence mode="wait">
          {featureCategories.map((category, index) => (
            <TabPanel key={category.label} value={activeTab} index={index}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Grid
                  container
                  spacing={6}
                  alignItems="center"
                  direction={index % 2 === 0 ? "row" : "row-reverse"}
                >
                  {/* Content */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Stack spacing={3}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: "#042E4B",
                        }}
                      >
                        {category.title}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color: "#64748B",
                          lineHeight: 1.7,
                        }}
                      >
                        {category.description}
                      </Typography>

                      <List sx={{ py: 0 }}>
                        {category.features.map((feature) => (
                          <ListItem
                            key={feature}
                            sx={{ px: 0, py: 0.75 }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircle
                                sx={{ color: "#10B981", fontSize: 22 }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              slotProps={{
                                primary: {
                                  variant: "body2",
                                  fontWeight: 500,
                                  color: "#334155",
                                },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Stack>
                  </Grid>

                  {/* Screenshot */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <ScreenshotImage
                      src={category.screenshot}
                      alt={category.screenshotAlt}
                      placeholderText={category.screenshotAlt}
                      minHeight={280}
                      boxShadow="0 24px 48px rgba(0,0,0,0.15)"
                    />
                  </Grid>
                </Grid>
              </motion.div>
            </TabPanel>
          ))}
        </AnimatePresence>
      </Container>
    </Box>
  );
};

export default FeaturesSection;
